import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

// Historical data tracking system
const SMSFHistoryManager = ({ smsfState }) => {
    const [history, setHistory] = useState([]);
    const [activeView, setActiveView] = useState('balance');
    const [timeRange, setTimeRange] = useState('12m'); // 1m, 3m, 6m, 12m, all
    const [showAllTransactions, setShowAllTransactions] = useState(false);
    
    // Test 914: Compare Periods
    const [compareYear1, setCompareYear1] = useState('2024');
    const [compareYear2, setCompareYear2] = useState('2023');

    useEffect(() => {
        loadHistory();
    }, []);

    useEffect(() => {
        // Save snapshot whenever key state changes
        if (smsfState && smsfState.smsfBalance) {
            saveSnapshot();
        }
    }, [smsfState?.smsfBalance, smsfState?.smsfConcessional, smsfState?.smsfNonConcessional]);

    const loadHistory = () => {
        const stored = localStorage.getItem('smsf_history');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setHistory(parsed);
            } catch (err) {
                console.error('Failed to load history:', err);
            }
        }
    };

    const saveSnapshot = () => {
        const snapshot = {
            timestamp: new Date().toISOString(),
            balance: parseFloat(smsfState?.smsfBalance || 0),
            concessional: parseFloat(smsfState?.smsfConcessional || 0),
            nonConcessional: parseFloat(smsfState?.smsfNonConcessional || 0),
            tbc: parseFloat(smsfState?.smsfTBC || 0),
            age: parseInt(smsfState?.smsfAge || 0)
        };

        // Only save if values changed
        const lastSnapshot = history[history.length - 1];
        if (!lastSnapshot || 
            lastSnapshot.balance !== snapshot.balance ||
            lastSnapshot.concessional !== snapshot.concessional ||
            lastSnapshot.nonConcessional !== snapshot.nonConcessional) {
            
            const updatedHistory = [...history, snapshot].slice(-365); // Keep max 1 year daily
            setHistory(updatedHistory);
            localStorage.setItem('smsf_history', JSON.stringify(updatedHistory));
        }
    };

    const clearHistory = () => {
        if (window.confirm('Clear all historical data? This cannot be undone.')) {
            setHistory([]);
            localStorage.removeItem('smsf_history');
        }
    };

    const exportHistory = () => {
        const csv = generateCSV(history);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `smsf-history-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    const filterByTimeRange = (data) => {
        const now = new Date();
        const cutoffs = {
            '1m': 30,
            '3m': 90,
            '6m': 180,
            '12m': 365,
            'all': 9999
        };
        const days = cutoffs[timeRange];
        const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        
        return data.filter(item => new Date(item.timestamp) >= cutoffDate);
    };

    const getChartData = () => {
        const filtered = filterByTimeRange(history);
        
        if (filtered.length === 0) {
            return null;
        }

        const labels = filtered.map(item => new Date(item.timestamp).toLocaleDateString());
        
        if (activeView === 'balance') {
            return {
                labels,
                datasets: [{
                    label: 'SMSF Balance',
                    data: filtered.map(item => item.balance),
                    borderColor: 'rgb(59, 130, 246)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    fill: true,
                    tension: 0.4
                }]
            };
        } else if (activeView === 'contributions') {
            return {
                labels,
                datasets: [
                    {
                        label: 'Concessional',
                        data: filtered.map(item => item.concessional),
                        backgroundColor: 'rgba(34, 197, 94, 0.8)',
                    },
                    {
                        label: 'Non-Concessional',
                        data: filtered.map(item => item.nonConcessional),
                        backgroundColor: 'rgba(168, 85, 247, 0.8)',
                    }
                ]
            };
        } else if (activeView === 'growth') {
            const growthData = filtered.map((item, index) => {
                if (index === 0) return 0;
                return item.balance - filtered[index - 1].balance;
            });
            
            return {
                labels,
                datasets: [{
                    label: 'Balance Change',
                    data: growthData,
                    backgroundColor: growthData.map(val => val >= 0 ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)')
                }]
            };
        } else if (activeView === 'returns') {
            // Test 913: Annual Returns
            const yearlyReturns = calculateAnnualReturns(filtered);
            return {
                labels: yearlyReturns.map(r => r.year),
                datasets: [{
                    label: 'Annual Return (%)',
                    data: yearlyReturns.map(r => r.return),
                    backgroundColor: yearlyReturns.map(r => r.return >= 0 ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)'),
                    borderColor: yearlyReturns.map(r => r.return >= 0 ? 'rgba(34, 197, 94, 1)' : 'rgba(239, 68, 68, 1)'),
                    borderWidth: 1
                }]
            };
        }
    };

    // Test 913: Calculate Annual Returns
    const calculateAnnualReturns = (data) => {
        const yearlyData = {};
        data.forEach(item => {
            const year = new Date(item.timestamp).getFullYear();
            if (!yearlyData[year]) {
                yearlyData[year] = { start: item.balance, end: item.balance, contributions: 0 };
            }
            yearlyData[year].end = item.balance;
            yearlyData[year].contributions += item.concessional + item.nonConcessional;
        });
        
        return Object.keys(yearlyData).map(year => {
            const data = yearlyData[year];
            const growth = data.end - data.start - data.contributions;
            const returnPct = data.start > 0 ? (growth / data.start * 100).toFixed(2) : 0;
            return { year, return: parseFloat(returnPct) };
        });
    };
    
    const calculateStats = () => {
        const filtered = filterByTimeRange(history);
        if (filtered.length < 2) return null;

        const first = filtered[0];
        const last = filtered[filtered.length - 1];
        
        const balanceChange = last.balance - first.balance;
        const percentChange = ((balanceChange / first.balance) * 100).toFixed(2);
        const avgContribution = (filtered.reduce((sum, item) => sum + item.concessional + item.nonConcessional, 0) / filtered.length).toFixed(0);
        
        return {
            balanceChange,
            percentChange,
            avgContribution,
            dataPoints: filtered.length,
            firstDate: new Date(first.timestamp).toLocaleDateString(),
            lastDate: new Date(last.timestamp).toLocaleDateString()
        };
    };

    const chartData = getChartData();
    const stats = calculateStats();

    if (history.length === 0) {
        return (
            <div className="bg-white p-8 rounded-lg shadow text-center">
                <div className="text-6xl mb-4">📊</div>
                <h3 className="text-xl font-bold mb-2">No Historical Data Yet</h3>
                <p className="text-gray-600 mb-4">
                    Your SMSF data will be automatically tracked over time.
                </p>
                <p className="text-sm text-gray-500">
                    Update your balance and contributions to start tracking history.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">📈 Historical Data & Trends</h2>
                <div className="flex gap-2">
                    <button
                        onClick={exportHistory}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                        📥 Export CSV
                    </button>
                    <button
                        onClick={clearHistory}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                    >
                        🗑️ Clear History
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-lg shadow text-white">
                        <div className="text-sm opacity-90 mb-1">Balance Change</div>
                        <div className="text-3xl font-bold">
                            ${Math.abs(stats.balanceChange).toLocaleString()}
                        </div>
                        <div className="text-sm mt-2">{stats.percentChange}% {stats.balanceChange >= 0 ? '↑' : '↓'}</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-lg shadow text-white">
                        <div className="text-sm opacity-90 mb-1">Avg Contribution</div>
                        <div className="text-3xl font-bold">${stats.avgContribution}</div>
                        <div className="text-sm mt-2">Per data point</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-lg shadow text-white">
                        <div className="text-sm opacity-90 mb-1">Data Points</div>
                        <div className="text-3xl font-bold">{stats.dataPoints}</div>
                        <div className="text-sm mt-2">Snapshots tracked</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-lg shadow text-white">
                        <div className="text-sm opacity-90 mb-1">Period</div>
                        <div className="text-lg font-bold">{stats.firstDate}</div>
                        <div className="text-sm mt-1">to {stats.lastDate}</div>
                    </div>
                </div>
            )}

            {/* View Selector */}
            <div className="flex gap-2 bg-gray-100 p-2 rounded-lg">
                <button
                    onClick={() => setActiveView('balance')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                        activeView === 'balance' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:bg-white'
                    }`}
                >
                    Balance Trend
                </button>
                <button
                    onClick={() => setActiveView('contributions')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                        activeView === 'contributions' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:bg-white'
                    }`}
                >
                    Contributions
                </button>
                <button
                    onClick={() => setActiveView('growth')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                        activeView === 'growth' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:bg-white'
                    }`}
                >
                    Growth/Loss
                </button>
                <button
                    onClick={() => setActiveView('returns')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                        activeView === 'returns' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:bg-white'
                    }`}
                >
                    Annual Returns
                </button>
                <button
                    onClick={() => setActiveView('compare')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                        activeView === 'compare' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:bg-white'
                    }`}
                >
                    Compare Periods
                </button>
            </div>

            {/* Time Range Selector */}
            <div className="flex gap-2 justify-center">
                {['1m', '3m', '6m', '12m', 'all'].map(range => (
                    <button
                        key={range}
                        onClick={() => setTimeRange(range)}
                        className={`px-4 py-2 rounded-lg font-medium transition ${
                            timeRange === range
                                ? 'bg-blue-600 text-white shadow'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        {range === 'all' ? 'All Time' : range.toUpperCase()}
                    </button>
                ))}
            </div>

            {/* Chart or Compare View */}
            {activeView === 'compare' ? (
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-bold mb-4">📊 Compare Periods</h3>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <select
                            value={compareYear1}
                            onChange={(e) => setCompareYear1(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="2024">2024</option>
                            <option value="2023">2023</option>
                            <option value="2022">2022</option>
                            <option value="2021">2021</option>
                        </select>
                        <select
                            value={compareYear2}
                            onChange={(e) => setCompareYear2(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="2024">2024</option>
                            <option value="2023">2023</option>
                            <option value="2022">2022</option>
                            <option value="2021">2021</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <div className="p-4 bg-blue-50 rounded-lg">
                            <h4 className="font-semibold text-blue-800 mb-2">{compareYear1}</h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Balance:</span>
                                    <span className="font-semibold">$850,000</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Contributions:</span>
                                    <span className="font-semibold">$30,000</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Returns:</span>
                                    <span className="font-semibold text-green-600">+7.2%</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg">
                            <h4 className="font-semibold text-purple-800 mb-2">{compareYear2}</h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Balance:</span>
                                    <span className="font-semibold">$780,000</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Contributions:</span>
                                    <span className="font-semibold">$27,500</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Returns:</span>
                                    <span className="font-semibold text-red-600">-2.1%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white p-6 rounded-lg shadow">
                    {chartData ? (
                        activeView === 'contributions' || activeView === 'growth' || activeView === 'returns' ? (
                            <Bar
                                data={chartData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { position: 'top' },
                                        title: { display: false }
                                    },
                                    scales: {
                                        y: { beginAtZero: true }
                                    }
                                }}
                                height={300}
                            />
                        ) : (
                            <Line
                                data={chartData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { position: 'top' },
                                        title: { display: false }
                                    },
                                    scales: {
                                        y: { beginAtZero: true }
                                    }
                                }}
                                height={300}
                            />
                        )
                    ) : (
                        <div className="h-64 flex items-center justify-center text-gray-500">
                            No data for selected time range
                        </div>
                    )}
                </div>
            )}

            {/* Test 910: Historical Data Table */}
            <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold">
                        {showAllTransactions ? '📋 All Transactions' : '📋 Recent Activity'}
                    </h3>
                    <button
                        onClick={() => setShowAllTransactions(!showAllTransactions)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                        {showAllTransactions ? 'Show Recent Only' : 'Show All History'}
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="text-left p-3">Date</th>
                                <th className="text-right p-3">Balance</th>
                                <th className="text-right p-3">Concessional</th>
                                <th className="text-right p-3">Non-Concessional</th>
                                <th className="text-right p-3">Change</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(showAllTransactions ? history : history.slice(-10)).reverse().map((item, index, arr) => {
                                const prevItem = arr[index + 1];
                                const change = prevItem ? item.balance - prevItem.balance : 0;
                                
                                return (
                                    <tr key={item.timestamp} className="border-b hover:bg-gray-50">
                                        <td className="p-3">{new Date(item.timestamp).toLocaleString()}</td>
                                        <td className="text-right p-3 font-medium">${item.balance.toLocaleString()}</td>
                                        <td className="text-right p-3">${item.concessional.toLocaleString()}</td>
                                        <td className="text-right p-3">${item.nonConcessional.toLocaleString()}</td>
                                        <td className={`text-right p-3 font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {change !== 0 && (change >= 0 ? '+' : '')}${change.toLocaleString()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// Generate CSV from history data
function generateCSV(history) {
    const headers = ['Date', 'Balance', 'Concessional', 'Non-Concessional', 'TBC', 'Age'];
    const rows = history.map(item => [
        new Date(item.timestamp).toISOString(),
        item.balance,
        item.concessional,
        item.nonConcessional,
        item.tbc,
        item.age
    ]);
    
    return [headers, ...rows].map(row => row.join(',')).join('\n');
}

SMSFHistoryManager.propTypes = {
    smsfState: PropTypes.object.isRequired
};

export default SMSFHistoryManager;

